import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useCallback, useEffect, useState } from "react";
import { Permissions } from "@src/domain/permissions/Permissions";

export interface OrganizationBrandStatusSwitchProps {
  brandId: number;
  brandIsActive: boolean;
  /** Called to perform the updat */
  onUpdate: (brandId: number, brandIsActive: boolean) => Promise<void>;
  /** Called after a successful update */
  onUpdateSuccess: () => void;
}

export const OrganizationBrandStatusSwitch = ({
  brandId,
  brandIsActive,
  onUpdate,
  onUpdateSuccess,
}: OrganizationBrandStatusSwitchProps) => {
  const [pendingActive, setPendingActive] = useState(brandIsActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(brandIsActive);
  }, [brandIsActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);
      try {
        await onUpdate(brandId, mode);
        onUpdateSuccess();
        successToast({
          content: "Brand status updated",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        setPendingActive(brandIsActive);
        console.error("Failed to update brand status", error);
        errorToast({
          content: "Failed to update brand status",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      brandId,
      brandIsActive,
      onUpdate,
      onUpdateSuccess,
      successToast,
      errorToast,
    ],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.BRANDS_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand status update"
          role="switch"
          htmlTitle="Update brand status"
        />
      </Flex>
    </HasAnyPermission>
  );
};
